import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { oneOf, readStored, writeStored } from "./store.js";
import { ACCENT_PRESETS, variablesFor } from "./accents.js";

/**
 * How the reader wants the app to look. Nothing here is telemetry.
 *
 * The design this is ported from mixed both into one context, with a
 * `setInterval` random-walking three "live" figures every four seconds to make
 * a demo feel alive. That half is deleted rather than adapted: the real
 * reading arrives over a stream and carries the server's own freshness
 * verdict, and a fabricated number on a dashboard whose job is to say when
 * something has stopped is the opposite of the point.
 */

export const THEMES = ["auto", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

export const DENSITIES = ["comfortable", "compact"] as const;
export type Density = (typeof DENSITIES)[number];

/**
 * The design's four colour accents. Each one is a measured palette rather than
 * the design's pair of raw hexes -- see `lib/accents.ts` for why, and
 * `tests/accents.test.ts` for the proof.
 */
export const ACCENTS = ACCENT_PRESETS;
export const ACCENT_IDS = ACCENT_PRESETS.map((preset) => preset.id) as readonly AccentId[];
export type AccentId = (typeof ACCENT_PRESETS)[number]["id"];

/**
 * The dashboard's widgets, in the design's own order and under its own ids.
 *
 * One for one with `AgtOwad/Q-Energy`. One of them has no data behind it yet
 * -- `weather` needs a source this deployment does not have -- and it is
 * present anyway, rendering its empty state, because the layout is the thing
 * being ported and a later slice fills the values in. A widget that appears later moves everything below it;
 * a widget that is already there does not.
 */
export const WIDGETS = [
  "welcome",
  "estFull",
  "battery",
  "totalEnergy",
  "loadBalance",
  "mainChart",
  "earning",
  "weather",
  "energyFlow",
] as const;
export type WidgetId = (typeof WIDGETS)[number];

export const DEFAULT_WIDGETS: readonly WidgetId[] = WIDGETS;

interface Preferences {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** What the theme actually resolves to right now, `auto` included. */
  resolved: "light" | "dark";
  density: Density;
  setDensity: (density: Density) => void;
  accent: AccentId;
  setAccent: (accent: AccentId) => void;
  widgets: readonly WidgetId[];
  toggleWidget: (id: WidgetId) => void;
  moveWidget: (id: WidgetId, direction: "up" | "down") => void;
  resetLayout: () => void;
}

const Context = createContext<Preferences | null>(null);

function parseWidgets(raw: string): readonly WidgetId[] | null {
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed)) return null;
  const known = parsed.filter((v): v is WidgetId => WIDGETS.includes(v as WidgetId));
  // A stored layout from a version with different widgets would otherwise
  // leave the reader with a blank dashboard and no way back to a full one.
  return known.length > 0 ? known : null;
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => readStored("theme", oneOf(THEMES)) ?? "auto",
  );
  const [density, setDensityState] = useState<Density>(
    () => readStored("density", oneOf(DENSITIES)) ?? "comfortable",
  );
  const [widgets, setWidgets] = useState<readonly WidgetId[]>(
    () => readStored("widgets", parseWidgets) ?? DEFAULT_WIDGETS,
  );
  const [accent, setAccentState] = useState<AccentId>(
    () => readStored("accent", oneOf(ACCENT_IDS)) ?? "emerald",
  );
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // `auto` has to keep following the operating system while the tab is open,
  // not only at mount -- otherwise a reader whose machine switches to dark at
  // sunset sits in light until they reload.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const resolved: "light" | "dark" = theme === "auto" ? (systemDark ? "dark" : "light") : theme;

  // The class the `dark:` variant keys off. Set on <html> rather than <body>
  // so a portalled dialog outside the body's subtree is themed too.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", resolved === "dark");
    root.classList.toggle("light", resolved === "light");
  }, [resolved]);

  // Persisted from effects, never from inside a state updater. The design
  // wrote storage inside its `setState` callbacks, which React invokes twice
  // under StrictMode -- so every interaction wrote twice, and the one early
  // return that skipped the write left storage stale.
  // The chosen accent, written onto the root element rather than into the
  // stylesheet. `emerald` restates exactly what `styles.css` declares, so the
  // default is the same colours whether or not this ever runs.
  useEffect(() => {
    const preset = ACCENT_PRESETS.find((candidate) => candidate.id === accent);
    if (preset === undefined) return;
    const root = document.documentElement;
    const variables = variablesFor(preset, resolved);
    for (const [name, value] of Object.entries(variables)) root.style.setProperty(name, value);
  }, [accent, resolved]);

  useEffect(() => writeStored("theme", theme), [theme]);
  useEffect(() => writeStored("accent", accent), [accent]);
  useEffect(() => writeStored("density", density), [density]);
  useEffect(() => writeStored("widgets", JSON.stringify(widgets)), [widgets]);

  const toggleWidget = useCallback((id: WidgetId) => {
    setWidgets((prev) => {
      if (!prev.includes(id)) return [...prev, id];
      // Never down to nothing: an empty dashboard offers no way to get a
      // widget back, since the control that would do it is on the dashboard.
      return prev.length <= 1 ? prev : prev.filter((w) => w !== id);
    });
  }, []);

  const moveWidget = useCallback((id: WidgetId, direction: "up" | "down") => {
    setWidgets((prev) => {
      const at = prev.indexOf(id);
      const to = direction === "up" ? at - 1 : at + 1;
      if (at < 0 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const moved = next[at];
      const displaced = next[to];
      // Both reads are checked because `noUncheckedIndexedAccess` is on, and
      // the checks are real: `at` and `to` are already bounded above.
      if (moved === undefined || displaced === undefined) return prev;
      next[at] = displaced;
      next[to] = moved;
      return next;
    });
  }, []);

  const resetLayout = useCallback(() => setWidgets(DEFAULT_WIDGETS), []);

  const value = useMemo<Preferences>(
    () => ({
      theme,
      setTheme: setThemeState,
      resolved,
      density,
      setDensity: setDensityState,
      accent,
      setAccent: setAccentState,
      widgets,
      toggleWidget,
      moveWidget,
      resetLayout,
    }),
    [theme, resolved, density, accent, widgets, toggleWidget, moveWidget, resetLayout],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePreferences(): Preferences {
  const value = useContext(Context);
  if (value === null) throw new Error("usePreferences needs a PreferencesProvider above it");
  return value;
}
